import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { setMarketingConsent } from "../utils/marketingMeasurement";
import "./CookieConsentBanner.css";

const LEGACY_STORAGE_KEY = "cookie_consent_v1";

function consentLabel(measurement) {
  return measurement ? "Zaakceptowano opcjonalne cookies." : "Opcjonalne cookies pozostają wyłączone.";
}

export default function CookieConsentBanner() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [decided, setDecided] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);
  const [manageExisting, setManageExisting] = useState(false);
  const [previewResult, setPreviewResult] = useState("");

  useEffect(() => {
    let active = true;

    async function loadConsent() {
      try {
        const response = await fetch("/api/marketing-measurement/consent", {
          credentials: "include",
          cache: "no-store",
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || "Nie udało się pobrać ustawień cookies.");
        if (!active) return;

        if (data.decided) {
          const accepted = data.measurement === true;
          setDecided(true);
          setMarketingConsent(accepted ? "accepted" : "rejected");
          try {
            window.localStorage.removeItem(LEGACY_STORAGE_KEY);
          } catch {}
          return;
        }

        let legacyChoice = null;
        try {
          legacyChoice = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        } catch {}

        if (legacyChoice === "accepted" || legacyChoice === "rejected") {
          const accepted = legacyChoice === "accepted";
          const migrationResponse = await fetch("/api/marketing-measurement/consent", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ measurement: accepted, action: "legacy_migration" }),
          });
          if (!migrationResponse.ok) throw new Error("Nie udało się przenieść ustawień cookies.");
          if (!active) return;
          try {
            window.localStorage.removeItem(LEGACY_STORAGE_KEY);
          } catch {}
          setDecided(true);
          setMarketingConsent(accepted ? "accepted" : "rejected");
          return;
        }

        setDecided(false);
      } catch (reason) {
        if (active) {
          setDecided(false);
          setError(reason?.message || "Nie udało się pobrać ustawień cookies.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadConsent();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    const showPreview = () => {
      setPreview(true);
      setPreviewResult("");
      setSettingsOpen(false);
      setError("");
    };
    window.addEventListener("matematron:privacy-preview", showPreview);
    return () => window.removeEventListener("matematron:privacy-preview", showPreview);
  }, []);

  useEffect(() => {
    const previewRequested = new URLSearchParams(location.search).get("cookiePreview") === "1";

    if (previewRequested) {
      setPreview(true);
      setPreviewResult("");
      setSettingsOpen(false);
      setError("");
    }
  }, [location.search]);

  useEffect(() => {
    const openSettings = () => {
      setPreview(false);
      setPreviewResult("");
      setManageExisting(true);
      setSettingsOpen(true);
      setError("");
    };
    window.addEventListener("matematron:privacy-settings", openSettings);
    return () => window.removeEventListener("matematron:privacy-settings", openSettings);
  }, []);

  const isVisible = useMemo(
    () => preview || manageExisting || (!loading && !decided),
    [preview, manageExisting, loading, decided]
  );

  const saveChoice = async (nextMeasurement, action) => {
    if (saving) return;
    setError("");
    if (preview) {
      setPreviewResult(consentLabel(nextMeasurement));
      return;
    }

    setSaving(true);
    try {
      const response = await fetch("/api/marketing-measurement/consent", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ measurement: nextMeasurement, action }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Nie udało się zapisać wyboru.");
      setMarketingConsent(nextMeasurement ? "accepted" : "rejected");
      setDecided(true);
      setManageExisting(false);
      setSettingsOpen(false);
    } catch (reason) {
      setError(reason?.message || "Nie udało się zapisać wyboru.");
    } finally {
      setSaving(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-consent-layer" role="presentation">
      <section
        className="cookie-consent-card"
        role="dialog"
        aria-modal="false"
        aria-label={settingsOpen ? "Ustawienia cookies" : "Informacja o plikach cookies"}
      >
        {preview && (
          <div className="cookie-consent-preview-bar">
            <span>Podgląd administratora — wybory nie są zapisywane</span>
            <button type="button" onClick={() => setPreview(false)} aria-label="Zamknij podgląd">×</button>
          </div>
        )}

        {!settingsOpen ? (
          <div className="cookie-consent-first-layer">
            <div className="cookie-consent-copy">
              <div className="cookie-consent-eyebrow">Twoja prywatność</div>
              <h2>Pomóż nam rozwijać Matematron</h2>
              <p>
                Niezbędne cookies zapewniają działanie platformy. Za Twoją zgodą użyjemy
                opcjonalnych cookies do pomiaru skuteczności kampanii i ulepszania serwisu.{" "}
                <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
              </p>
            </div>
            <div className="cookie-consent-main-actions">
              <button
                type="button"
                className="btn-secondary cookie-consent-learn-more"
                disabled={saving}
                onClick={() => {
                  setPreviewResult("");
                  setSettingsOpen(true);
                }}
              >
                Dowiedz się więcej
              </button>
              <button
                type="button"
                className="btn-primary cookie-consent-accept"
                disabled={saving}
                onClick={() => saveChoice(true, "accepted_all")}
              >
                Akceptuję i przechodzę dalej
              </button>
            </div>
          </div>
        ) : (
          <div className="cookie-consent-settings">
            <div className="cookie-consent-settings-header">
              <div>
                <div className="cookie-consent-eyebrow">Twój wybór</div>
                <h2>Ustawienia cookies</h2>
                <p>Możesz korzystać z Matematronu bez opcjonalnych cookies.</p>
              </div>
              <button
                type="button"
                className="cookie-consent-back"
                onClick={() => {
                  setPreviewResult("");
                  if (manageExisting) setManageExisting(false);
                  else setSettingsOpen(false);
                }}
              >
                Wróć
              </button>
            </div>

            <div className="cookie-consent-options">
              <div className="cookie-consent-option">
                <div>
                  <strong>Niezbędne</strong>
                  <span>Logowanie, bezpieczeństwo i zapamiętanie Twojej decyzji.</span>
                </div>
                <span className="cookie-consent-required">Zawsze aktywne</span>
              </div>

              <div className="cookie-consent-option">
                <div>
                  <strong>Pomiar kampanii i ulepszanie platformy</strong>
                  <span>Pomaga ocenić skuteczność reklam Google i korzystanie z serwisu.</span>
                </div>
                <span className="cookie-consent-optional">Opcjonalne</span>
              </div>
            </div>

            <details className="cookie-consent-details">
              <summary>Jakie dane mogą być wykorzystywane?</summary>
              <p>
                Opcjonalny pomiar obejmuje źródło wejścia, identyfikator kampanii, odwiedzane
                widoki oraz podstawowe informacje techniczne o urządzeniu. Dane potrzebne do
                działania konta i zapisywania postępów są przetwarzane osobno, niezależnie od
                tego ustawienia.
              </p>
            </details>
            <details className="cookie-consent-details">
              <summary>Jak zapisujemy Twój wybór?</summary>
              <p>
                Decyzja jest przypisana do tej przeglądarki. Po zalogowaniu łączymy ją z kontem;
                na innym urządzeniu poprosimy o wybór ponownie.
              </p>
            </details>

            <div className="cookie-consent-settings-actions">
              <button
                type="button"
                className="cookie-consent-essential-only"
                disabled={saving}
                onClick={() => saveChoice(false, "rejected_optional")}
              >
                Przechodzę tylko z niezbędnymi
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={() => saveChoice(true, "accepted_all")}
              >
                Akceptuję wszystkie i przechodzę dalej
              </button>
            </div>
          </div>
        )}

        {previewResult && <div className="cookie-consent-preview-result">{previewResult}</div>}
        {error && <div className="cookie-consent-error">{error}</div>}
      </section>
    </div>
  );
}
